import {
	type OrderDetailResponseDto,
	OrderPartnerReviewRatingEnum,
	type PartnerReviewResponseDto,
} from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useDeletePartnerReviewMutation } from "Order/Api/Mutation/useDeletePartnerReviewMutation";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { Delete, ThumbDown, ThumbUp } from "@mui/icons-material";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { Fragment } from "react";

const reviewRatingSetup = {
	[OrderPartnerReviewRatingEnum.satisfied]: {
		icon: <ThumbUp />,
		color: "success.main",
	},
	[OrderPartnerReviewRatingEnum.not_satisfied]: {
		icon: <ThumbDown />,
		color: "error.main",
	},
};

const { partnerReviewShared } = Resources.base.order;

type OrderReviewsListWidgetProps = {
	order: OrderDetailResponseDto;
	reviews: PartnerReviewResponseDto[];
};

export function OrderReviewsListWidget({
	order,
	reviews,
}: OrderReviewsListWidgetProps) {
	const { t } = useResource();

	const { data: partners } = usePartnersListQuery({
		marketId: order.market?.id,
	});

	const { mutate: deleteReview, isPending: isPendingDeleteReview } =
		useDeletePartnerReviewMutation(order.id);

	const hasDeletePermission = hasPermission(
		UserPermissions.OrderPartnerReviewModule.delete,
	);

	const handleDeleteReview = (reviewId: number) => {
		hasDeletePermission && deleteReview(reviewId);
	};

	return (
		<BaseCard title={t(partnerReviewShared.title)}>
			<Stack direction="column" spacing={2}>
				{reviews?.map((review, index) => {
					const partnerData = partners?.data?.find(
						({ partner }) => partner.id === review.partnerId,
					)?.partner;

					const partnerFullName =
						`${partnerData?.name ?? ""} ${partnerData?.middleName ?? ""} ${partnerData?.surname ?? ""}`.trim();

					return (
						<Fragment key={review.id}>
							<Stack direction="row" justifyContent="space-between">
								<Stack direction="column" spacing={1}>
									<Stack direction="row" spacing={2} alignItems="center">
										<Typography variant="h6">{partnerFullName}</Typography>
										{review.rating && (
											<Typography
												color={reviewRatingSetup[review.rating].color}
											>
												{reviewRatingSetup[review.rating].icon}
											</Typography>
										)}
									</Stack>
									{review.description && (
										<Typography>{review.description}</Typography>
									)}
								</Stack>
								<IconButton
									size="small"
									onClick={() => handleDeleteReview(review.id)}
									loading={isPendingDeleteReview}
									sx={{ width: 32, height: 32, color: "warning.main" }}
								>
									<Delete />
								</IconButton>
							</Stack>
							{index !== reviews.length - 1 && <Divider />}
						</Fragment>
					);
				})}
			</Stack>
		</BaseCard>
	);
}
